import { Directive, ElementRef, input, output } from '@angular/core'
import { filter, first, fromEvent, map, race, takeUntil, timer } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { isNotNullOrUndefined } from '../../lib/util/null-utils'
import { DestroySubscribers } from '../../util'

const DEFAULT_LONG_PRESS_THRESHOLD_IN_MS = 500

@Directive({
  standalone: true,
  selector: '[longPress]',
})
export class LongPressDirective extends DestroySubscribers {
  longPressThresholdMs = input(DEFAULT_LONG_PRESS_THRESHOLD_IN_MS)

  longPress = output<PointerEvent>()

  constructor({ nativeElement }: ElementRef) {
    super()

    const pointerDown$ = fromEvent<PointerEvent>(nativeElement, 'pointerdown').pipe(filter(isTouchEvent))
    const pointerUp$ = fromEvent<PointerEvent>(nativeElement, 'pointerup').pipe(filter(isTouchEvent))
    const pointerMove$ = fromEvent<PointerEvent>(nativeElement, 'pointermove').pipe(filter(isTouchEvent))

    pointerDown$
      .pipe(
        switchMap(startEvent =>
          race([
            pointerUp$.pipe(map(() => null)),
            pointerMove$.pipe(map(() => null)),
            timer(this.longPressThresholdMs()).pipe(map(() => startEvent)),
          ]).pipe(first()),
        ),
        filter(isNotNullOrUndefined),
        takeUntil(this.finalize),
      )
      .subscribe(event => this.longPress.emit(event))

    // Prevent text selection while long pressing
    pointerDown$.pipe(takeUntil(this.finalize)).subscribe(() => {
      // safari
      nativeElement.style.webkitTouchCallout = 'none'
      nativeElement.style.webkitUserSelect = 'none'
      // everything-else
      nativeElement.style.userSelect = 'none'
    })
    pointerUp$.pipe(takeUntil(this.finalize)).subscribe(() => {
      // safari
      nativeElement.style.webkitTouchCallout = 'auto'
      nativeElement.style.webkitUserSelect = 'auto'
      // everything-else
      nativeElement.style.userSelect = 'auto'
    })
  }
}

function isTouchEvent(event: PointerEvent): boolean {
  return event.pointerType === 'touch'
}
